import { useEffect, useState, useRef } from 'react'
import { MapPin, ChevronDown, Check } from 'lucide-react'
import { useSiteStore } from '../../stores/siteStore'
import { cn } from '../../lib/utils'

export default function SiteSwitcher() {
  const { sites, activeSiteId, loaded, loadSites, setActiveSite, activeSite } = useSiteStore()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!loaded) loadSites()
  }, [loaded, loadSites])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  if (sites.length <= 1) return null

  const current = activeSite()

  const switchSite = async (siteId: string) => {
    setOpen(false)
    await setActiveSite(siteId)
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors"
      >
        <MapPin size={14} />
        <span className="max-w-[140px] truncate">{current?.name}</span>
        <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-bg-card border border-border rounded-xl shadow-lg z-50 py-1 max-h-80 overflow-y-auto">
          <div className="px-3 py-2 text-xs text-text-muted uppercase tracking-wider">Sites</div>
          {sites.map(site => (
            <button
              key={site.id}
              onClick={() => switchSite(site.id)}
              className={cn(
                'w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-bg-hover transition-colors',
                site.id === activeSiteId && 'bg-accent/5',
              )}
            >
              <MapPin size={14} className="text-text-muted shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary truncate">{site.name}</p>
                {(site.city || site.state) && (
                  <p className="text-xs text-text-muted">{[site.city, site.state].filter(Boolean).join(', ')}</p>
                )}
              </div>
              {site.id === activeSiteId && <Check size={14} className="text-accent shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
